/**
 * AssetActivationWizard — Aktivierung Beleg → Anlagegut
 *
 * Geöffnet aus ExpenseDetail.jsx, wenn Beleg über GWG-Grenze und Anlage-fähig ist.
 * Pre-fillt Werte aus Expense, lässt User ND/Methode/Startdatum/Privat-Anteil
 * anpassen, zeigt Live-Plan-Vorschau, bei Bestätigung wird der Beleg aktiviert.
 */
import { useEffect, useMemo, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Boxes, Loader2, Sparkles, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/utils/api";
import AfaPlanPreviewTable from "./AfaPlanPreviewTable";

export default function AssetActivationWizard({ open, onClose, expense, country = "DE", aiCategoryHint = null, onActivated, apiClient = api, afaTableUrl = "/afa-table", previewUrl = "/fixed-assets/preview-plan", activateUrl = null, notifyOption = false }) {
  const [table, setTable] = useState({ entries: [], gwg: {} });
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Initialisiere Form aus Expense
  useEffect(() => {
    if (!open || !expense) return;
    const gross = parseFloat(expense.amount || 0);
    const net = parseFloat(
      expense.net_amount != null
        ? expense.net_amount
        : (expense.vat_rate ? gross / (1 + expense.vat_rate / 100) : gross),
    );
    const vat = parseFloat((gross - net).toFixed(2));
    const acquisitionDate = (expense.date || "").slice(0, 10);
    // Feb 2026 — KI-Hint für Kategorie übernehmen wenn vorhanden
    const initialCategory = aiCategoryHint || "computer_notebook";
    setForm({
      name: expense.description || expense.vendor || "Anlage",
      category_key: initialCategory,
      acquisition_date: acquisitionDate,
      depreciation_start_date: acquisitionDate,
      cost_net: net,
      cost_gross: gross,
      vat_amount: vat,
      vat_rate: expense.vat_rate || 19,
      vendor: expense.vendor || "",
      useful_life_years: 3,
      depreciation_method: country === "CH" ? "degressive" : "linear",
      degressive_rate: country === "CH" ? 0.40 : null,
      residual_value: 0,
      private_use_percentage: 0,
      notes: "",
      notify_client: notifyOption,
      notify_message: "",
    });
  }, [open, expense, country, aiCategoryHint]);

  // Lade AfA-Tabelle für aktuelles Land
  useEffect(() => {
    if (!open) return;
    apiClient
      .get(`${afaTableUrl}?country=${country}`)
      .then((res) => {
        setTable(res.data);
        // Feb 2026 — Wenn KI-Hint da ist: ND/Methode aus Tabelle übernehmen
        if (aiCategoryHint) {
          const entry = res.data.entries.find((e) => e.key === aiCategoryHint);
          if (entry) {
            setForm((f) => ({
              ...f,
              category_key: aiCategoryHint,
              useful_life_years: entry.years || f.useful_life_years,
              depreciation_method: entry.method || f.depreciation_method,
              degressive_rate: entry.rate ?? f.degressive_rate,
            }));
          }
        }
      })
      .catch(() => setTable({ entries: [], gwg: {} }));
  }, [open, country, aiCategoryHint]);

  // Bei Kategorie-Wechsel: ND-Vorschlag aus Tabelle übernehmen
  const onCategoryChange = (key) => {
    const entry = table.entries.find((e) => e.key === key);
    setForm((f) => ({
      ...f,
      category_key: key,
      useful_life_years: entry?.years || f.useful_life_years,
      depreciation_method: entry?.method || f.depreciation_method,
      degressive_rate: entry?.rate ?? null,
    }));
  };

  const handleActivate = async () => {
    if (!expense?.id) {
      toast.error("Beleg nicht gefunden");
      return;
    }
    setSaving(true);
    try {
      const url = activateUrl || `/expenses/${expense.id}/activate-as-asset`;
      const res = await apiClient.post(url, form);
      toast.success(`Anlage „${form.name}" aktiviert · ${res.data.postings} AfA-Buchungen vorgemerkt`);
      onActivated?.(res.data.asset);
      onClose?.();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      toast.error(`Aktivierung fehlgeschlagen: ${msg}`);
    } finally {
      setSaving(false);
    }
  };

  if (!expense) return null;

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose?.()}>
      <SheetContent className="w-full sm:max-w-[640px] overflow-y-auto" data-testid="asset-activation-wizard">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Boxes className="h-5 w-5 text-emerald-600" />
            Als Anlage aktivieren
          </SheetTitle>
          <SheetDescription>
            Dieser Beleg wird in ein Wirtschaftsgut umgewandelt und über die Nutzungsdauer abgeschrieben.
            Die Vorsteuer bleibt unverändert in der UStVA aktiv.
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-4 pt-4 pb-8">
          {/* Info-Banner */}
          {aiCategoryHint && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex gap-2 text-sm" data-testid="wizard-ai-hint">
              <Sparkles className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-medium text-emerald-900">KI-Vorschlag übernommen</div>
                <div className="text-emerald-700 text-xs mt-0.5">
                  Kategorie <strong>{table.entries.find((e) => e.key === aiCategoryHint)?.label || aiCategoryHint}</strong> + Nutzungsdauer-Vorschlag aus AfA-Tabelle eingesetzt. Du kannst alles anpassen.
                </div>
              </div>
            </div>
          )}
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex gap-2 text-sm">
            <Sparkles className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-medium text-emerald-900">GWG-Grenze {country}: {table.gwg?.instant_max?.toFixed(0) || 800} {table.gwg?.currency || "EUR"}</div>
              <div className="text-emerald-700 text-xs mt-0.5">
                Belege ab dieser Grenze sind in der Regel über mehrere Jahre abzuschreiben.
              </div>
            </div>
          </div>

          {/* Bezeichnung */}
          <div>
            <Label>Bezeichnung</Label>
            <Input
              value={form.name || ""}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="wizard-name"
            />
          </div>

          {/* Kategorie */}
          <div>
            <Label>Kategorie</Label>
            <Select value={form.category_key || ""} onValueChange={onCategoryChange}>
              <SelectTrigger data-testid="wizard-category">
                <SelectValue placeholder="Kategorie wählen" />
              </SelectTrigger>
              <SelectContent>
                {table.entries.map((e) => (
                  <SelectItem key={e.key} value={e.key}>
                    {e.label} — Vorschlag: {e.years} J {e.method === "degressive" ? "degressiv" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Anschaffungs-Datum + AfA-Start */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Anschaffungsdatum</Label>
              <Input
                type="date"
                value={form.acquisition_date || ""}
                onChange={(e) => setForm({ ...form, acquisition_date: e.target.value, depreciation_start_date: e.target.value })}
                data-testid="wizard-acquisition-date"
              />
            </div>
            <div>
              <Label className="flex items-center gap-1">
                AfA-Beginn
                <span className="text-[10px] text-slate-400">(frei wählbar)</span>
              </Label>
              <Input
                type="date"
                value={form.depreciation_start_date || ""}
                onChange={(e) => setForm({ ...form, depreciation_start_date: e.target.value })}
                data-testid="wizard-start-date"
              />
            </div>
          </div>

          {/* Beträge — read-only */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Anschaffungskosten netto</Label>
              <Input value={`${(form.cost_net || 0).toFixed(2)} €`} disabled data-testid="wizard-cost-net" />
            </div>
            <div>
              <Label>Vorsteuer (UStVA-relevant)</Label>
              <Input value={`${(form.vat_amount || 0).toFixed(2)} €`} disabled />
            </div>
          </div>

          {/* ND + Methode */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Nutzungsdauer (Jahre)</Label>
              <Input
                type="number"
                min={1}
                max={50}
                value={form.useful_life_years || ""}
                onChange={(e) => setForm({ ...form, useful_life_years: parseInt(e.target.value) || 1 })}
                data-testid="wizard-useful-life"
              />
            </div>
            <div>
              <Label>Methode</Label>
              <Select
                value={form.depreciation_method || "linear"}
                onValueChange={(v) => {
                  setForm((f) => {
                    let rate = f.degressive_rate;
                    if (v === "degressive" && !rate) {
                      if (country === "CH") rate = 0.40;
                      else if (country === "AT") rate = 0.30;
                      else rate = Math.min(2.5 / Math.max(f.useful_life_years || 1, 1), 0.25);
                      rate = Math.round(rate * 10000) / 10000;
                    }
                    return { ...f, depreciation_method: v, degressive_rate: rate };
                  });
                }}
              >
                <SelectTrigger data-testid="wizard-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="linear">Linear</SelectItem>
                  <SelectItem value="degressive">Degressiv (vom Buchwert)</SelectItem>
                  <SelectItem value="pool_5y" disabled={country !== "DE"}>Sammelposten (5J, nur DE)</SelectItem>
                  <SelectItem value="gwg_immediate">GWG (Sofortabschreibung)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {form.depreciation_method === "degressive" && (
            <div>
              <Label>Degressiver Satz (% vom Buchwert/Jahr)</Label>
              <Input
                type="number"
                min={1}
                max={50}
                step="1"
                value={form.degressive_rate ? (form.degressive_rate * 100).toFixed(0) : ""}
                onChange={(e) => setForm({ ...form, degressive_rate: parseFloat(e.target.value) / 100 || 0.4 })}
                data-testid="wizard-degressive-rate"
              />
              <p className="text-xs text-slate-500 mt-1">
                CH-Standard: PC/Pkw 40 %, Mobiliar 25 %, Maschinen 30 %.
              </p>
            </div>
          )}

          {/* Privat-Anteil */}
          <div>
            <Label className="flex items-center justify-between">
              <span>Privat-Anteil: <strong>{form.private_use_percentage || 0} %</strong></span>
              {form.private_use_percentage > 0 && (
                <span className="text-xs text-amber-700 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  § 15a UStG-Berichtigung aktiv
                </span>
              )}
            </Label>
            <Slider
              value={[form.private_use_percentage || 0]}
              onValueChange={(v) => setForm({ ...form, private_use_percentage: v[0] })}
              min={0}
              max={99}
              step={5}
              data-testid="wizard-private-use"
            />
            <p className="text-xs text-slate-500 mt-1">
              Bei gemischter Nutzung (z.B. Pkw 30 % privat) wird Vorsteuer und AfA-Aufwand anteilig gekürzt.
            </p>
          </div>

          {/* Live-Plan-Vorschau */}
          <AfaPlanPreviewTable
            costNet={form.cost_net}
            country={country}
            method={form.depreciation_method}
            years={form.useful_life_years}
            startDate={form.depreciation_start_date}
            residual={form.residual_value}
            degressiveRate={form.degressive_rate}
            privateUsePercentage={form.private_use_percentage}
            apiClient={apiClient}
            previewUrl={previewUrl}
          />

          {/* IAB/IFB-Info-Banner (Phase 5, MVP-Light gemäß Frage 9A) */}
          {(country === "DE" || country === "AT") && form.cost_net >= 800 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800" data-testid="iab-ifb-banner">
              <div className="font-medium mb-0.5">
                💡 {country === "DE" ? "IAB nach § 7g EStG" : "IFB nach § 11 EStG"} möglich
              </div>
              <div>
                {country === "DE"
                  ? "Bis zu 50 % der Anschaffungskosten könnten als Investitionsabzugsbetrag (IAB) bereits im Anschaffungsjahr abgesetzt werden — bei Erfüllung der Voraussetzungen (Gewinngrenze, betriebliche Nutzung ≥ 90 %)."
                  : "Bis zu 15 % der Anschaffungskosten können als Investitionsfreibetrag (IFB) im Anschaffungsjahr zusätzlich abgesetzt werden."}
                {" "}Voll-Verzahnung mit dem Anlagen-Modul ist Backlog — bitte mit Steuerberater klären.
              </div>
            </div>
          )}

          {/* Juni 2026 — Optionale Mandanten-Benachrichtigung (nur SB-Modus) */}
          {notifyOption && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2" data-testid="wizard-notify-block">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!form.notify_client}
                  onChange={(e) => setForm({ ...form, notify_client: e.target.checked })}
                  className="mt-0.5 h-4 w-4 accent-blue-600"
                  data-testid="wizard-notify-checkbox"
                />
                <span className="text-sm text-blue-900">
                  Mandant über die Aktivierung informieren
                  <span className="block text-xs text-blue-700">
                    Postet eine Notiz im Posteingang des Mandanten (verlinkt mit diesem Beleg).
                  </span>
                </span>
              </label>
              {form.notify_client && (
                <Input
                  value={form.notify_message || ""}
                  onChange={(e) => setForm({ ...form, notify_message: e.target.value })}
                  placeholder="Optionale persönliche Nachricht (optional)…"
                  className="bg-white"
                  data-testid="wizard-notify-message"
                />
              )}
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-2 pt-2">
            <Button
              className="flex-1 bg-emerald-600 hover:bg-emerald-700"
              onClick={handleActivate}
              disabled={saving || !form.name || !form.useful_life_years}
              data-testid="wizard-activate-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Boxes className="h-4 w-4 mr-2" />}
              {saving ? "Aktiviere..." : "Anlage aktivieren"}
            </Button>
            <Button variant="outline" onClick={onClose} disabled={saving}>
              Abbrechen
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
